import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CalculatorIcon, 
  ArrowTrendingUpIcon,
  CurrencyEuroIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const ProfitCalculator = () => {
  const [nftCount, setNftCount] = useState(1);
  const [selectedProject, setSelectedProject] = useState('tier0');
  const [selectedRarity, setSelectedRarity] = useState('common');
  const [ethUsdPrice, setEthUsdPrice] = useState(3184.58); // Current ETH/USD price
  const [calculatedReturns, setCalculatedReturns] = useState({
    monthly: 0,
    yearly: 0,
    totalInvestment: 0,
    baseInvestment: 0
  });

  const nftPriceEth = 0.24; // Price per NFT in ETH

  const rarityMultipliers = {
    common: {
      name: "Green Card",
      multiplier: 1.0,
      description: "Standard investment power",
      rarity: "60%"
    },
    rare: {
      name: "Bronze Card",
      multiplier: 1.2,
      description: "1.2x investment power",
      rarity: "30%"
    },
    epic: {
      name: "Silver Card",
      multiplier: 1.5,
      description: "1.5x investment power", 
      rarity: "8%"
    },
    legendary: {
      name: "Gold Card",
      multiplier: 2.0,
      description: "2x investment power",
      rarity: "2%"
    }
  };

  const projectTypes = {
    tier0: {
      name: "Tier 0 Projects",
      returnRate: 0.02, // 2% monthly
      description: "Entry-level European real estate projects",
      investmentPerNFT: 4000
    },
    tier1: {
      name: "Tier 1 Projects",
      returnRate: 0.04, // 4% monthly
      description: "Standard European real estate projects",
      investmentPerNFT: 4000
    },
    tier2: {
      name: "Tier 2 Projects", 
      returnRate: 0.06, // 6% monthly
      description: "Premium location properties",
      investmentPerNFT: 4000
    },
    tier3: {
      name: "Tier 3 Projects",
      returnRate: 0.08, // 8% monthly  
      description: "High-end luxury developments",
      investmentPerNFT: 4000
    }
  };

  // Fetch ETH price from Kraken public API
  useEffect(() => {
    const fetchEthPrice = async () => {
      try {
        const response = await fetch('https://api.kraken.com/0/public/Ticker?pair=ETHUSD');
        const data = await response.json();
        if (data.result && data.result.XETHZUSD && data.result.XETHZUSD.c) {
          const price = parseFloat(data.result.XETHZUSD.c[0]);
          setEthUsdPrice(price);
        }
      } catch (error) {
        console.error('Error fetching ETH price:', error);
      }
    };

    fetchEthPrice();
    const interval = setInterval(fetchEthPrice, 3600000); // Update every hour
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const project = projectTypes[selectedProject];
    const rarity = rarityMultipliers[selectedRarity];
    
    // Base investment = number of NFTs × 0.24 ETH × current ETH/USD price
    const baseInvestment = nftCount * nftPriceEth * ethUsdPrice;
    
    // Enhanced investment = base investment × rarity multiplier
    const enhancedInvestment = baseInvestment * rarity.multiplier;
    
    // Monthly return based on project tier return rate
    const monthlyReturn = enhancedInvestment * project.returnRate;
    const yearlyReturn = monthlyReturn * 12;

    setCalculatedReturns({
      monthly: monthlyReturn,
      yearly: yearlyReturn,
      totalInvestment: enhancedInvestment,
      baseInvestment: baseInvestment
    });
  }, [nftCount, selectedProject, selectedRarity, ethUsdPrice]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <section id="calculator" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 bg-purple-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-cyan-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Profit Calculator
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Calculate your potential returns based on NFT holdings and project participation. 
            With our 2-8% monthly project returns, one card can pay for itself within the first year!
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Calculator Input - Left Side */}
            <motion.div
              className="bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 border border-gray-700"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-3 mb-8">
                <CalculatorIcon className="w-8 h-8 text-purple-400" />
                <h3 className="text-3xl font-bold text-white">Investment Calculator</h3>
              </div>

              {/* NFT Count Selector */}
              <div className="mb-8">
                <label className="block text-gray-300 mb-4 text-lg font-semibold">
                  Number of NFTs (1-3)
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {[1, 2, 3].map((count) => (
                    <motion.button
                      key={count}
                      onClick={() => setNftCount(count)}
                      className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                        nftCount === count
                          ? 'border-purple-500 bg-purple-500/20 text-white'
                          : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-gray-500'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="text-3xl font-bold mb-2">{count}</div>
                      <div className="text-sm">NFT{count > 1 ? 's' : ''}</div>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Project Type Dropdown */}
              <div className="mb-8">
                <label className="block text-gray-300 mb-4 text-lg font-semibold">
                  Project Type
                </label>
                <div className="relative">
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl px-4 py-4 text-white font-semibold focus:border-cyan-500 focus:outline-none transition-colors duration-200 appearance-none cursor-pointer"
                  >
                    {Object.entries(projectTypes).map(([key, project]) => (
                      <option key={key} value={key} className="bg-gray-800">
                        {project.name} - {(project.returnRate * 100).toFixed(0)}% Monthly Return
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-2">{projectTypes[selectedProject].description}</p>
              </div>

              {/* Card Rarity Dropdown */}
              <div className="mb-8">
                <label className="block text-gray-300 mb-4 text-lg font-semibold">
                  Card Rarity
                </label>
                <div className="relative">
                  <select
                    value={selectedRarity}
                    onChange={(e) => setSelectedRarity(e.target.value)}
                    className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl px-4 py-4 text-white font-semibold focus:border-purple-500 focus:outline-none transition-colors duration-200 appearance-none cursor-pointer"
                  >
                    {Object.entries(rarityMultipliers).map(([key, rarity]) => (
                      <option key={key} value={key} className="bg-gray-800">
                        {rarity.name} - {rarity.multiplier}x Power ({rarity.rarity})
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-2">{rarityMultipliers[selectedRarity].description}</p>
              </div>

              {/* Investment Breakdown */}
              <div className="bg-gray-700/50 rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Investment Breakdown</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">NFT Cost (0.24 ETH each):</span>
                    <span className="text-white font-semibold">{(nftCount * 0.24).toFixed(2)} ETH</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Base Investment Power:</span>
                    <span className="text-purple-400 font-semibold">{formatCurrency(calculatedReturns.baseInvestment)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-600 pt-3">
                    <span className="text-gray-300">Rarity Multiplier ({rarityMultipliers[selectedRarity].name}):</span>
                    <span className="text-cyan-400 font-semibold">{rarityMultipliers[selectedRarity].multiplier}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Enhanced Investment Power:</span>
                    <span className="text-green-400 font-semibold">{formatCurrency(calculatedReturns.totalInvestment)}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Results Display - Right Side */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {/* Monthly Returns */}
              <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 backdrop-blur-lg rounded-3xl p-8 border border-green-500/30">
                <div className="flex items-center space-x-3 mb-4">
                  <ArrowTrendingUpIcon className="w-8 h-8 text-green-400" />
                  <h3 className="text-2xl font-bold text-white">Monthly Returns</h3>
                </div>
                <div className="text-5xl font-bold text-green-400 mb-2">
                  {formatCurrency(calculatedReturns.monthly)}
                </div>
                <p className="text-gray-300">
                  Projected monthly profit from your {nftCount} Card{nftCount > 1 ? 's' : ''}
                </p>
              </div>

              {/* Yearly Returns */}
              <div className="bg-gradient-to-r from-purple-600/20 to-violet-600/20 backdrop-blur-lg rounded-3xl p-8 border border-purple-500/30">
                <div className="flex items-center space-x-3 mb-4">
                  <ChartBarIcon className="w-8 h-8 text-purple-400" />
                  <h3 className="text-2xl font-bold text-white">Annual Returns</h3>
                </div>
                <div className="text-5xl font-bold text-purple-400 mb-2">
                  {formatCurrency(calculatedReturns.yearly)}
                </div>
                <p className="text-gray-300">
                  Projected annual profit with {projectTypes[selectedProject].name.toLowerCase()}
                </p>
              </div>

              {/* ROI Calculation */}
              <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-lg rounded-3xl p-8 border border-cyan-500/30">
                <div className="flex items-center space-x-3 mb-4">
                  <CurrencyEuroIcon className="w-8 h-8 text-cyan-400" />
                  <h3 className="text-2xl font-bold text-white">ROI Analysis</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-2xl font-bold text-cyan-400">
                      {((calculatedReturns.yearly / calculatedReturns.baseInvestment) * 100).toFixed(2)}%
                    </div>
                    <div className="text-gray-300 text-sm">Return on Base Investment</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cyan-400">
                      {(calculatedReturns.yearly / calculatedReturns.totalInvestment * 100).toFixed(2)}%
                    </div>
                    <div className="text-gray-300 text-sm">Return on Enhanced Investment</div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-black/20 rounded-xl">
                  <p className="text-gray-300 text-sm">
                    * Calculations based on {(projectTypes[selectedProject].returnRate * 100).toFixed(0)}% monthly project returns and 
                    {rarityMultipliers[selectedRarity].multiplier}x rarity multiplier. 
                    One {rarityMultipliers[selectedRarity].name} can pay for itself in approximately 
                    {(calculatedReturns.baseInvestment / calculatedReturns.yearly).toFixed(1)} years with these returns!
                  </p>
                </div>
              </div>

              {/* CTA */}
              <motion.a
                href="https://og.rarible.com/token/0xb66a603f4cfe17e3d27b87a8bfcad319856518b8:66443457087237224376496623462586478899471087482130632045442236354399968428034"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-amber-600 to-blue-600 hover:from-amber-700 hover:to-blue-700 text-white py-6 rounded-2xl text-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-amber-500/25 text-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Earning - Mint Your Cards Now
              </motion.a>
            </motion.div>
          </div>

          {/* Disclaimer */}
          <motion.div
            className="mt-12 bg-yellow-600/10 border border-yellow-500/20 rounded-2xl p-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h4 className="text-yellow-400 font-semibold mb-2">Investment Disclaimer</h4>
            <p className="text-gray-300 text-sm leading-relaxed">
              All projected returns are estimates based on market analysis and historical data. 
              Real estate investments carry inherent risks, and actual returns may vary. 
              Past performance does not guarantee future results. Please invest responsibly and 
              consider consulting with a financial advisor.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProfitCalculator;